import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import { DataGrid, ptBR } from "@mui/x-data-grid";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { getDoctors } from "~/services/antecipebackoffice/doctors";
import CustomPagination from "~/components/CustomPagination/index";
import EditDoctorBankModal from "~/components/modals/EditDoctorBankModal";
import EditDoctorPJModal from "~/components/modals/EditDoctorPJModal";
import FilterBarDoctors from "~/components/FilterBarDoctors";
import ReadOnlyChip from "~/components/ReadOnlyChip";
import { useAuth } from "~/auth/AuthProvider";
import PageHeader from "~/shared/ui/PageHeader";
import EmptyState from "~/shared/ui/EmptyState";
import ErrorState from "~/shared/ui/ErrorState";
import { getApiErrorMessage } from "~/utils/apiError";
import { useLatestRequest } from "~/shared/ui/filters/useLatestRequest";

const LIMIT = 10;

function DoctorActions({ row, onOpen }) {
  const [anchor, setAnchor] = useState(null);
  const pick = (type) => {
    setAnchor(null);
    onOpen(type, row);
  };
  return (
    <>
      <Tooltip title="Ações">
        <IconButton aria-label={`Ações do médico ${row.name}`} aria-haspopup="menu" onClick={(e) => setAnchor(e.currentTarget)} sx={{ width: 40, height: 40 }}>
          <MoreVertIcon />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
        <MenuItem onClick={() => pick("bank")}>Dados Bancários</MenuItem>
        <MenuItem onClick={() => pick("pj")}>Dados Pessoa Jurídica</MenuItem>
      </Menu>
    </>
  );
}

export default function TableDoctors() {
  const { permissions } = useAuth();
  const readOnly = !permissions.backofficeWrite;
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({});
  const [order, setOrder] = useState("asc");
  const [modal, setModal] = useState(null); // { type: "bank" | "pj", doctor }
  // Muda ao limpar pelo estado vazio: remonta a barra para os campos refletirem os filtros aplicados.
  const [filterBarKey, setFilterBarKey] = useState(0);

  const beginRequest = useLatestRequest();
  const getData = async (pageSelected = page, f = filters, o = order) => {
    // Última requisição vence: resposta de um filtro antigo não sobrescreve a do atual (decisão U1).
    const isLatest = beginRequest();
    setLoading(true);
    setError(null);
    try {
      const response = await getDoctors({ limit: LIMIT, offset: (pageSelected - 1) * LIMIT }, { ...f, orderById: o });
      if (!isLatest()) return;
      setTotal(response.data.total_items);
      setRows(response.data.data);
    } catch (e) {
      if (isLatest()) setError(getApiErrorMessage(e, "Não foi possível carregar os médicos."));
    } finally {
      if (isLatest()) setLoading(false);
    }
  };

  useEffect(() => {
    getData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = (params) => {
    setPage(1);
    setFilters(params);
    getData(1, params, order);
  };

  const clearFromEmptyState = () => {
    setFilterBarKey((k) => k + 1);
    applyFilters({});
  };

  const columns = [
    { field: "id", headerName: "ID", width: 90, sortable: true },
    { field: "name", headerName: "Nome", flex: 1, minWidth: 200, sortable: false },
    { field: "email", headerName: "Email", flex: 1, minWidth: 220, sortable: false },
    { field: "crm", headerName: "CRM", width: 130, sortable: false, valueGetter: ({ row }) => [row.crm, row.crm_uf].filter(Boolean).join("/") },
    { field: "cpf", headerName: "CPF", width: 140, sortable: false },
    {
      field: "actions", headerName: "Ações", width: 80, sortable: false, align: "center", headerAlign: "center",
      renderCell: ({ row }) => <DoctorActions row={row} onOpen={(type, doctor) => setModal({ type, doctor })} />,
    },
  ];

  const empty = !loading && !error && rows.length === 0;

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", gap: 1.5, p: { xs: 1.5, md: 3 }, minHeight: 0 }}>
      <PageHeader title="Dados do Médico" description="Dados bancários e de pessoa jurídica dos médicos que pediram antecipação." actions={readOnly ? <ReadOnlyChip /> : null} />
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <FilterBarDoctors key={filterBarKey} onFilterChange={applyFilters} />
      </Paper>
      <Paper variant="outlined" sx={{ flex: 1, minHeight: 420, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {error ? (
          <ErrorState description={error} onRetry={() => getData()} />
        ) : empty ? (
          <EmptyState title="Nenhum médico encontrado com esses filtros" description="Revise nome, CRM ou CPF." actions={<Button variant="contained" onClick={clearFromEmptyState}>Limpar filtros</Button>} />
        ) : (
          <DataGrid
            localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
            rows={rows}
            columns={columns}
            loading={loading}
            density="standard"
            // 44px: comporta os alvos de 40px das ações (spec a11y) sem invadir a linha vizinha
            rowHeight={44}
            headerHeight={40}
            disableVirtualization
            disableColumnMenu
            disableColumnFilter
            disableSelectionOnClick
            sortingMode="server"
            sortModel={[{ field: "id", sort: order }]}
            onSortModelChange={(model) => {
              const next = model[0]?.sort || (order === "desc" ? "asc" : "desc");
              setOrder(next);
              setPage(1);
              getData(1, filters, next);
            }}
            components={{
              Pagination: () => (
                <CustomPagination count={Math.ceil(total / LIMIT)} page={page} setPage={setPage} onChange={(event, value) => getData(value)} />
              ),
            }}
            sx={{ border: 0, flex: 1 }}
          />
        )}
      </Paper>
      {modal?.type === "bank" && (
        <EditDoctorBankModal open setOpen={() => setModal(null)} idDoctor={modal.doctor.id} doctorName={modal.doctor.name} readOnly={readOnly} />
      )}
      {modal?.type === "pj" && (
        <EditDoctorPJModal open setOpen={() => setModal(null)} idDoctor={modal.doctor.id} doctorName={modal.doctor.name} readOnly={readOnly} />
      )}
    </Box>
  );
}
